import { mat4, ReadonlyMat4 } from 'gl-matrix';
import { ChangeTracker } from '../changes/ChangeTracker';
import { RegistrationSnapshot, TrackableObject } from '../changes/DomainChange';
import { newRegistrationId, RegistrationId } from '../changes/ids';
import { GenerationModelDetails } from '../dicom/dicomBase';
import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { DicomRegistration } from '../dicom/dicomRegistration';
import { DicomRigidRegistration } from '../dicom/dicomRigidRegistration';
import { ApprovedRegistrationError } from '../errors/DomainError';
import { domainEventBus } from '../events/DomainEventBus';
import { RegistrationMatrixCommittedEvent } from '../events/RegistrationDomainEvents';

export class RigidRegistration implements TrackableObject {
    readonly trackableKind = 'registration' as const;
    readonly id: RegistrationId = newRegistrationId();
    private tracker: ChangeTracker | null = null;
    public readonly transformation: mat4;
    public readonly sourceFrameOfReferenceUid: string;
    public readonly targetFrameOfReferenceUid: string;
    public readonly dicomParent: DicomRegistration;

    private _approvalStatus: DICOMApprovalStatus =
        DICOMApprovalStatus.UNAPPROVED;

    /**
     * Approval is APPLICATION STATE ONLY: the DICOM Spatial Registration IOD
     * has no approval attribute, so reviewer and status are never serialized
     * into the REG dataset or export blobs — the connector database row is
     * their sole persistence.
     */
    private _reviewerName = '';

    /**
     * Session-monotonic counter of committed matrix edits (manual
     * translate/rotate, elastix results, undo/redo restores). A PHI-safe
     * count: comparing it against a baseline detects "matrix changed since
     * load" exactly. Deliberately never decremented — an undo back to the
     * loaded matrix still counts (worst case an identical matrix is
     * re-saved, never a missed save).
     */
    private _transformRevision = 0;

    constructor(
        sourceFrameOfReferenceUid: string,
        targetFrameOfReferenceUid: string,
        transformation: mat4,
        parent: DicomRegistration
    ) {
        this.sourceFrameOfReferenceUid = sourceFrameOfReferenceUid;
        this.targetFrameOfReferenceUid = targetFrameOfReferenceUid;
        this.transformation = transformation;
        this.dicomParent = parent;
    }

    get trackableId(): RegistrationId {
        return this.id;
    }

    attachTracker(tracker: ChangeTracker): void {
        this.tracker = tracker;
    }

    captureSnapshot(): RegistrationSnapshot {
        return {
            id: this.id,
            sopInstanceUid: this.sopInstanceUid,
            contentLabel: this.contentLabel,
            contentDescription: this.contentDescription,
            contentCreatorName: this.contentCreatorName,
            contentDate: this.contentDate,
            sourceFrameOfReferenceUid: this.sourceFrameOfReferenceUid,
            targetFrameOfReferenceUid: this.targetFrameOfReferenceUid,
            approvalStatus: this.approvalStatus,
            reviewerName: this._reviewerName,
            isApproved: this.isApproved,
            isNew: this.isNew,
            transformRevision: this._transformRevision,
        };
    }

    /**
     * The registration's display name.
     *
     * ContentLabel (0070,0080) is CS — 16 characters — but the patient
     * database's row label, the rename dialog and the connector's own writer
     * all work in LO (64), and the writer puts the name in ContentDescription /
     * SeriesDescription rather than ContentLabel. Reading ContentLabel alone
     * therefore showed any legal 17-64 character name truncated to 16, and a
     * later rename would PATCH that truncated string back, permanently
     * shortening a stored name the user never saw in full. ContentDescription
     * is the LO-wide carrier; ContentLabel remains the fallback for a parsed
     * REG that has no description.
     */
    public get contentLabel(): string {
        return this.rowLabel ?? this.dicomParent.contentLabel;
    }

    public get contentDescription(): string {
        return this.dicomParent.contentDescription;
    }

    public rename(label: string): void {
        this.ensureCanModify();
        // The full name lives here; the CS16 tag keeps its truncated form,
        // which is what ContentLabel's VR actually permits.
        this.rowLabel = label;
        this.dicomParent.contentLabel = label;
        this.tracker?.markChanged(this);
    }

    public setDescription(description: string): void {
        this.ensureCanModify();
        this.dicomParent.contentDescription = description;
        this.tracker?.markChanged(this);
    }

    public get contentCreatorName(): string {
        return this.dicomParent.contentCreatorName;
    }

    public get contentDate(): string {
        return this.dicomParent.contentDate;
    }

    public get sopInstanceUid(): string {
        return this.dicomParent.sopInstanceUid;
    }

    public get dicomUid(): string {
        return this.sopInstanceUid;
    }

    /**
     * The patient database row's display name, when this registration has a
     * row. The name is LO (64) there, in the rename dialog and in the
     * connector's writer, but the DICOM ContentLabel tag it is mirrored into is
     * CS (16). Keeping the full value here means a legal 17-64 character name
     * displays and re-PATCHes intact, while the exported tag stays within its
     * VR — reading the tag back as the name truncated it to 16 and a later
     * rename wrote that truncation back, permanently shortening a stored name
     * the user never saw in full.
     */
    private rowLabel: string | null = null;

    /** Adopts the row's display name on load, without marking the registration changed. */
    public adoptPersistedLabel(label: string): void {
        this.rowLabel = label;
        this.dicomParent.contentLabel = label;
    }

    /**
     * Records the identity the patient database minted for this registration's
     * stored file — the whole of it, since a registration created in-session
     * gets server Study/Series UIDs too, not just a SOP UID on later edits.
     * Editing the transform produces a NEW instance,
     * so an export still emitting the loaded UID would let two different
     * objects — the stored one and anything exported from here — claim one
     * DICOM identity, which a downstream PACS resolves by treating them as the
     * same instance and keeping whichever it saw first.
     *
     * The UID goes into the DICOM dataset and its file meta, which is what
     * `getAsDicomBlob` serializes, and DELIBERATELY NOT into
     * {@link sopInstanceUid}. That property is this object's SESSION identity:
     * `selectedRegistrationState`, the sidebar projection, the undo history and
     * every `getRegistrationWithSopInstanceUid` lookup are keyed by it, and
     * adoption bypasses the change tracker by design. Rewriting it mid-session
     * orphans all of them at once — a post-save Approve resolves to nothing and
     * drops the sign-off with no feedback, which is a worse defect than the
     * divergence it would fix. The two identities re-converge on reload, where
     * the registration is parsed from the stored file.
     */
    public adoptPersistedFileIdentity(identity: {
        readonly studyInstanceUid?: string | null;
        readonly seriesInstanceUid?: string | null;
        readonly sopInstanceUid?: string | null;
    }): void {
        const wrapper = this.dicomParent.datasetWrapper;
        if (identity.studyInstanceUid) {
            wrapper.setStudyInstanceUid(identity.studyInstanceUid);
        }
        if (identity.seriesInstanceUid) {
            wrapper.setSeriesInstanceUid(identity.seriesInstanceUid);
        }
        if (identity.sopInstanceUid) {
            wrapper.setSopInstanceUid(identity.sopInstanceUid);
        }
    }

    public get generationModel(): GenerationModelDetails | undefined {
        return this.dicomParent.generationModel;
    }

    public get isNew(): boolean {
        return this.dicomParent.isNew;
    }

    public get isExported(): boolean {
        return this.dicomParent.isExported;
    }

    public setExported(): void {
        // Mark before mutating — see DoseMatrix.setExported.
        this.tracker?.markChanged(this);
        this.dicomParent.setExported();
    }

    public get approvalStatus(): DICOMApprovalStatus {
        return this._approvalStatus;
    }

    public get isApproved(): boolean {
        return this._approvalStatus === DICOMApprovalStatus.APPROVED;
    }

    public get reviewerName(): string {
        return this._reviewerName;
    }

    public approve(reviewer: string): void {
        this._approvalStatus = DICOMApprovalStatus.APPROVED;
        this._reviewerName = reviewer;
        this.tracker?.markChanged(this);
    }

    public reject(reviewer: string): void {
        this._approvalStatus = DICOMApprovalStatus.REJECTED;
        this._reviewerName = reviewer;
        this.tracker?.markChanged(this);
    }

    public unapprove(): void {
        this._approvalStatus = DICOMApprovalStatus.UNAPPROVED;
        this._reviewerName = '';
        this.tracker?.markChanged(this);
    }

    public get transformRevision(): number {
        return this._transformRevision;
    }

    /**
     * Records a committed matrix edit for dirty tracking. Must run inside a
     * tracker command — call through
     * {@link RegistrationService.commitMatrixEdit}, which the
     * RegistrationEditBridge drives off {@link RegistrationMatrixCommittedEvent}.
     * Throws on an approved registration: the engine guards should have
     * prevented the edit, so reaching here approved is a bug that must
     * surface, never a silently untracked change.
     */
    public markMatrixEdited(): void {
        this.ensureCanModify();
        this._transformRevision++;
        this.tracker?.markChanged(this);
    }

    /**
     * Writes the live matrix into the DICOM dataset and, when the value
     * actually changed, publishes {@link RegistrationMatrixCommittedEvent} —
     * the single dirty-tracking funnel for matrix edits (manual tools commit
     * here on mouse release/keypress; elastix and undo/redo call it too).
     */
    public syncTransformationToDataset(): void {
        if (!(this.dicomParent instanceof DicomRigidRegistration)) {
            return;
        }
        if (this.dicomParent.syncTransformationToDataset()) {
            // Domain id only — PHI-safe (never the SOP UID or label).
            // Synchronous dispatch: the pending-changes classification must
            // reflect the commit before the caller's next read of it.
            domainEventBus.publishSync(
                new RegistrationMatrixCommittedEvent(this.id)
            );
        }
    }

    /**
     * Whether a manual alignment made on the image in `frameOfReferenceUid`
     * writes this registration's own matrix. A pair whose fixed image sits
     * in the target frame is displayed through the inverse matrix, and a
     * drag made from that side would edit the registration backwards.
     */
    public editableFrom(frameOfReferenceUid: string): boolean {
        return this.sourceFrameOfReferenceUid === frameOfReferenceUid;
    }

    /**
     * Returns the transformation matrix that maps points FROM `fromFOR` space
     * INTO `intoFOR` space. Returns null if this registration doesn't link
     * the two frames of reference.
     *
     * @param fromFOR Frame of Reference UID of the coordinate space to transform FROM
     * @param intoFOR Frame of Reference UID of the coordinate space to transform INTO
     */
    public getTransformFromTo(
        fromFOR: string,
        intoFOR: string
    ): ReadonlyMat4 | undefined {
        if (fromFOR === intoFOR) {
            return mat4.create();
        }
        if (
            fromFOR === this.targetFrameOfReferenceUid &&
            intoFOR === this.sourceFrameOfReferenceUid
        ) {
            return this.transformation;
        }
        if (
            fromFOR === this.sourceFrameOfReferenceUid &&
            intoFOR === this.targetFrameOfReferenceUid
        ) {
            // mat4.invert returns null for a singular transform; surface
            // that as "no usable transform" so callers never receive a
            // null outside the ReadonlyMat4 | undefined contract.
            const inverse = mat4.invert(mat4.create(), this.transformation);
            return inverse ?? undefined;
        }
        return undefined;
    }

    private ensureCanModify(): void {
        if (this._approvalStatus === DICOMApprovalStatus.APPROVED) {
            throw new ApprovedRegistrationError(
                `Cannot modify approved registration '${this.contentLabel}'`
            );
        }
    }
}
